import React, { createRef, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Box, Grid } from '@mui/material';
import { Formik, setIn } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { InputFieldFactory } from 'app/components/FormikField';
import { actions as InsurersActions, selectors as insurersSelector } from 'app/Domain/Insurers/Ducks/Insurers.duck';
import { actions as InsurerActions, selectors as insurerSelector } from 'app/Domain/Insurers/Ducks/Insurer.duck';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty, isNil } from 'app/utils';
import CollapsableSection from 'app/components/CollapsableSection';
import { actions as AppActions } from 'app/Domain/App/Ducks/App.duck';
import { mapSettingsField, SettingsFieldGroup } from 'app/components/FormikField/Factory/SettingsFieldFactory';
import { onboardingService } from '../../../../Connections/Services';
import { formatDateTime } from 'app/utils/formatter/dateTimeFormatter';
import { TranslationKeys } from '../../../../../translations';
import Typography from '@mui/material/Typography';
import { FormLabelStyled } from '../../../Pages/EmployerAddPage/EmployerAddPage.styles';

const ConnectionInfoStep = forwardRef(({ onSubmit, stepState, connection, loading = false }, ref) => {
  const parseConnectionDate = dateValue => (dateValue !== undefined && dateValue !== null ? new Date(dateValue) : null);

  const connectionLatestRun = connection ? parseConnectionDate(connection.latestRun) : null;
  const connectionNextRun = connection ? parseConnectionDate(connection.nextRun) : null;

  const getProductTypeLabel = type => {
    const labels = {
      WGA_GAT: 'WGA-gat verzekering',
      WGA_ERD: 'WGA-Eigen Risicoverzekering',
      WIA_Excendent: 'WIA-excedentverzekering',
      IVA_Excedent: 'IVA Excedent',
      WIA_Bodem: 'Inkomensaanvulling < 35% AO',
      sickLeave: 'Ziekengeldverzekering',
    };
    return labels[type] || type;
  };

  const formRef = createRef();
  const intl = useIntl();
  const dispatch = useDispatch();
  const [initialLoading, setInitialLoading] = useState(true);
  const [credentialSettingsLoading, setCredentialSettingsLoading] = useState(
    stepState.state.credentialSettingsLoading ?? false,
  );
  const [credentialSettings, setCredentialSettings] = useState(stepState.state.credentialSettings ?? null);
  const [selectedProductEntityId, setSelectedProductEntityId] = useState(
    stepState.state.selectedProductEntityId ?? (connection?.product && connection?.product['@id']) ?? null,
  );
  const [selectedProduct, setSelectedProduct] = useState(
    stepState.state.selectedProduct ?? connection?.product ?? null,
  );
  const [insurerId, setInsurerId] = useState(
    stepState.state.insurerId ?? connection?.product?.insurer?.insurerId ?? null,
  );
  const [productTypes, setProductTypes] = useState(stepState.state.productTypes ?? []);
  const [connectionName, setConnectionName] = useState(
    stepState.state.connectionName ?? connection?.connectionName ?? null,
  );
  const [disabled, setDisabled] = useState(stepState.state.disabled ?? connection?.disabled ?? false);
  const { loading: insurersLoading, items: insurers } = useSelector(state =>
    insurersSelector.selectInsurers(state.InsurersReducer),
  );
  const { loading: productsLoading, items: products } = useSelector(state =>
    insurerSelector.selectInsurerProducts(state.InsurerReducer),
  );

  // Convert connection productTypes (strings) to productIds on initial load
  useEffect(() => {
    if (connection?.productTypes && products && products.length > 0 && productTypes.length === 0) {
      const productTypesMap = products.flatMap(product =>
        (product.productType || []).map(type => ({
          type,
          productId: product.productId,
        })),
      );

      const productIds = connection.productTypes
        .map(typeString => {
          const match = productTypesMap.find(item => item.type === typeString);
          return match ? match.productId : null;
        })
        .filter(Boolean);

      if (productIds.length > 0) {
        setProductTypes(productIds);
      }
    }
  }, [connection?.productTypes, products, productTypes.length]);

  const selectedInsurer = useMemo(() => {
    return (insurers || []).find(insurer => insurer.insurerId === insurerId) ?? null;
  }, [insurers, insurerId]);

  const showProductTypesSelector = useMemo(() => {
    return selectedInsurer?.multipleProductType === true;
  }, [selectedInsurer]);

  const insurerFieldLoading = loading || initialLoading || insurersLoading;
  const productsFieldLoading = insurerFieldLoading || productsLoading;
  const productHasSettings = !isEmpty(selectedProduct?.credential);

  useImperativeHandle(
    ref,
    () => ({
      submit() {
        if (credentialSettingsLoading || productsFieldLoading) {
          return;
        }
        formRef.current.submitForm();
      },
      getState() {
        return {
          state: {
            credentialSettings,
            selectedProduct,
            selectedProductEntityId,
            insurerId,
            productTypes,
            connectionName,
            disabled,
            credentialSettingsLoading,
          },
          form: formRef.current.values,
        };
      },
    }),
    [
      connectionName,
      disabled,
      credentialSettings,
      credentialSettingsLoading,
      formRef,
      insurerId,
      productTypes,
      productsFieldLoading,
      selectedProduct,
      selectedProductEntityId,
    ],
  );

  useEffect(() => {
    if (initialLoading) {
      dispatch(InsurersActions.insurers.requestData());
      setInitialLoading(false);
    }
  }, [dispatch, initialLoading]);

  useEffect(() => {
    if (insurerId) {
      dispatch(InsurerActions.products.requestData({ insurerId }));
    }
  }, [dispatch, insurerId]);

  useEffect(() => {
    const product = (products || []).find(p => p['@id'] === selectedProductEntityId) ?? null;
    if (product !== selectedProduct) {
      setSelectedProduct(product);
    }
  }, [products, selectedProduct, selectedProductEntityId]);

  useEffect(() => {
    if (!selectedProduct || !productHasSettings) {
      setCredentialSettings(null);
      return;
    }
    if (!!credentialSettings && selectedProduct.credential.credentialId === credentialSettings.credentialId) {
      return;
    }
    setCredentialSettingsLoading(true);
    onboardingService
      .getCredentialSettings({
        credentialId: selectedProduct.credential.credentialId,
        connectionId: connection?.connectionId ?? null,
      })
      .then(result => {
        setCredentialSettings({
          credentialId: selectedProduct.credential.credentialId,
          data: result.data['hydra:member'],
        });
      })
      .catch(() => {
        return dispatch(AppActions.displayError({ method: 'getCredentialSettings' }));
      })
      .finally(() => {
        setCredentialSettingsLoading(false);
      });
  }, [credentialSettings, dispatch, productHasSettings, selectedProduct, connection?.connectionId]);

  const changeConnectionName = useCallback(({ value: connectionName }) => {
    setConnectionName(connectionName);
  }, []);

  const changeDisabled = useCallback(({ value: disabled }) => {
    setDisabled(disabled);
  }, []);

  const changeInsurer = useCallback(({ value: insurerId }) => {
    setInsurerId(insurerId);
    setSelectedProduct(null);
    setProductTypes([]);
  }, []);

  const changeProductTypes = useCallback(({ value: productTypes }) => {
    const values = Array.isArray(productTypes)
      ? productTypes.map(item => (typeof item === 'object' && item.value ? item.value : item))
      : [];
    setProductTypes(values);
  }, []);

  const changeProduct = useCallback(
    ({ value: productEntityId }) => {
      const newProduct = (products || []).find(p => p['@id'] === productEntityId) ?? null;
      if (newProduct?.['@id']) {
        setProductTypes(prevTypes => prevTypes.filter(id => id !== newProduct.productId));
      }
      setSelectedProductEntityId(productEntityId);
    },
    [products],
  );

  const fields = useMemo(
    () => ({
      connectionName: {
        name: 'connectionName',
        type: 'text',
        required: true,
        header: 'products.connectionName',
        placeholder: intl.formatMessage({ id: 'products.connectionName' }),
        loading: loading,
        schema: schema =>
          schema
            .trim()
            .strict()
            .max(255)
            .test('no-emojis', 'No emojis allowed', val => {
              if (!val) return true;
              const emojiRegex =
                /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/;
              return !emojiRegex.test(val);
            }),
      },
      insurerId: {
        name: 'insurerId',
        type: 'autocomplete',
        required: true,
        header: 'products.selectInsurer',
        placeholder: intl.formatMessage({ id: 'products.insurerName' }),
        loading: insurerFieldLoading,
        items: (insurers || []).map(insurer => ({
          element: `${insurer.insurerName} [${insurer.type.toUpperCase()}]`,
          value: insurer.insurerId,
        })),
      },
      productEntityId: {
        name: 'productEntityId',
        type: 'autocomplete',
        required: true,
        header: 'products.selectProduct',
        placeholder: intl.formatMessage({ id: 'products.productName' }),
        loading: productsFieldLoading,
        items: productsFieldLoading
          ? []
          : (products || []).map(product => ({
              element: product.productName,
              value: product['@id'],
            })),
      },
      disabled: {
        name: 'disabled',
        type: 'boolean',
        required: true,
        header: 'products.disabled',
        placeholder: intl.formatMessage({ id: 'products.disabled' }),
        loading: loading,
      },
      productTypes: {
        name: 'productTypes',
        type: 'multi-select',
        required: false,
        header: 'products.selectProductTypes',
        loading: productsFieldLoading,
        items: productsFieldLoading
          ? []
          : (products || [])
              .flatMap(product =>
                (product.productType || []).map(type => ({
                  type,
                  productId: product.productId,
                })),
              )
              .filter(({ type }) => {
                const selectedProductType = selectedProduct?.productType?.[0];
                return type !== selectedProductType;
              })
              .map(({ type, productId }) => ({
                element: getProductTypeLabel(type),
                value: productId,
              })),
      },
    }),
    [intl, loading, insurerFieldLoading, insurers, productsFieldLoading, products, selectedProduct],
  );

  const settingsFields = useMemo(() => {
    if (!productHasSettings || isEmpty(credentialSettings)) {
      return [];
    }
    const credentialSettingsData = credentialSettings.data || [];

    return credentialSettingsData.reduce((result, settings) => {
      const currentSettings = connection?.packages?.find(p => p.packageType === settings.packageType)?.settings || {};
      const settingsFields = mapSettingsField(
        intl,
        settings,
        'onboarding.settings.',
        credentialSettingsLoading,
        stepState.form,
        currentSettings,
        SettingsFieldGroup.Insurer,
      );
      return result.concat(settingsFields);
    }, []);
  }, [productHasSettings, credentialSettings, connection?.packages, intl, credentialSettingsLoading, stepState.form]);

  const initialValues = useMemo(() => {
    let values = {
      connectionName: connectionName,
      insurerId: insurerId,
      productEntityId: isNil(selectedProduct) ? null : selectedProduct['@id'],
      productTypes: productTypes,
      disabled: disabled,
    };

    const credential = selectedProduct?.credential || null;
    if (credential) {
      values = setIn(values, `credentials.${credential.packageType}`, credential.credentialId);
    }

    settingsFields.forEach(field => {
      values = setIn(values, field.name, field.value ?? field.defaultValue ?? null);
    });

    return values;
  }, [connectionName, insurerId, selectedProduct, productTypes, disabled, settingsFields]);

  const handleSubmit = useCallback(
    values => {
      const productTypesMap = (products || []).flatMap(product =>
        (product.productType || []).map(type => ({
          type,
          productId: product.productId,
        })),
      );

      const selectedProductIds = values.productTypes || [];

      const selectedProductTypeStrings = selectedProductIds
        .map(selectedProductId => {
          const match = productTypesMap.find(item => item.productId === selectedProductId);
          return match ? match.type : null;
        })
        .filter(Boolean);

      const processedValues = {
        ...values,
        productTypes: selectedProductTypeStrings,
        productsIds: selectedProductIds,
      };

      if (formRef.current) {
        Object.keys(processedValues).forEach(key => {
          formRef.current.setFieldValue(key, processedValues[key], false);
        });
      }

      return onSubmit(processedValues);
    },
    [products, onSubmit, formRef],
  );

  return (
    <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit} innerRef={formRef}>
      {({ values }) => (
        <>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <InputFieldFactory field={fields['connectionName']} onChange={changeConnectionName} />
            </Grid>
            <Grid item xs={4}>
              <InputFieldFactory field={fields['insurerId']} onChange={changeInsurer} />
            </Grid>
            <Grid item xs={4}>
              <InputFieldFactory
                field={fields['productEntityId']}
                onChange={changeProduct}
                disabled={!insurers || isEmpty(values.insurerId)}
              />
            </Grid>
            {showProductTypesSelector && (
              <Grid item xs={4}>
                <InputFieldFactory
                  field={fields['productTypes']}
                  onChange={changeProductTypes}
                  disabled={!insurers || isEmpty(values.insurerId)}
                />
              </Grid>
            )}
          </Grid>
          <Box p={1} />

          <CollapsableSection header={'onboarding.connectionRun'} hidden={false} expanded={true}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <InputFieldFactory field={fields['disabled']} onChange={changeDisabled} />
              </Grid>
              {connection && (
                <Grid item xs={4}>
                  <FormLabelStyled>
                    <FormattedMessage id={TranslationKeys.connection_latestRun} />
                  </FormLabelStyled>
                  <Typography variant="body1">{formatDateTime(connectionLatestRun)}</Typography>
                </Grid>
              )}
              {connection && (
                <Grid item xs={4}>
                  <FormLabelStyled>
                    <FormattedMessage id={TranslationKeys.connection_nextRun} />
                  </FormLabelStyled>
                  <Typography variant="body1">{formatDateTime(connectionNextRun)}</Typography>
                </Grid>
              )}
            </Grid>
          </CollapsableSection>
          <Box p={1} />
          <CollapsableSection
            header={'onboarding.insurerStep'}
            loading={credentialSettingsLoading}
            expanded={!credentialSettingsLoading && !isEmpty(settingsFields)}
            hidden={!productHasSettings}
            controllable
          >
            {productHasSettings && (
              <>
                <Box p={1} />
                <Grid container spacing={2}>
                  {settingsFields.map((field, index) => (
                    <Grid item key={index} xs={4}>
                      <InputFieldFactory field={field} />
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </CollapsableSection>
        </>
      )}
    </Formik>
  );
});

ConnectionInfoStep.displayName = 'ConnectionInfoStep';

export default ConnectionInfoStep;
